import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import iconExpandDownCapital from '../../assets/img/icon-chevron.svg';
import InputErrorIcon from '../../assets/img/input-error-icon.svg';
import ClickableDiv from '../../shared_components/ClickableDiv/ClickableDiv';
import useAnimatedMount, { AnimatedMountStatus } from '../../shared_components/hooks/useAnimatedMount';
import useClickOutside from '../../shared_components/hooks/useClickOutside';
import Country from '../Country/Country';

import { ICountryControlOptions, ICountryControlProps } from './CountryControl.types';

import styles from './CountryControl.module.scss';

const NO_COUNTRY_FLAG_ID = 'WD';

const CountryControl = (
  {
    id,
    value,
    options,
    placeholder,
    errors = [],
    className,
    onCountryChange,
    ariaLabel,
    onSubmit,
    icon,
  }: ICountryControlProps,
): JSX.Element => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [isControlDirty, setIsControlDirty] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<ICountryControlOptions | null>(null);
  const [query, setQuery] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);

  const listboxId = `${id ?? 'country'}-listbox`;

  const { isMounted, status } = useAnimatedMount(isOpen);

  const closeDropdown = () => {
    setIsOpen(false);
    setQuery(selectedOption?.label ?? '');
  };

  useClickOutside(wrapperRef, closeDropdown);

  useEffect(() => {
    if (!isControlDirty && (options.length > 0) && value) {
      let optionToEmit = options[0];

      if (value.trim() !== '') {
        const filteredOption = options.find((option: ICountryControlOptions) => (
          option.value.toString().toLowerCase() === value.toLowerCase()));

        optionToEmit = filteredOption ?? options[0];
      }

      setSelectedOption(optionToEmit);
      setQuery(optionToEmit.label);
    }
  }, [isControlDirty, options, value]);

  const filteredOptions = useMemo((): ICountryControlOptions[] => {
    const search = query.trim().toLowerCase();
    const isSearching = search !== '' && search !== (selectedOption?.label ?? '').toLowerCase();

    if (!isSearching) {
      return options;
    }

    return options.filter((option: ICountryControlOptions) => option.label.toLowerCase().startsWith(search));
  }, [options, query, selectedOption]);

  const closingOptionsRef = useRef<ICountryControlOptions[]>(filteredOptions);

  if (isOpen) {
    closingOptionsRef.current = filteredOptions;
  }

  const renderedOptions = isOpen ? filteredOptions : closingOptionsRef.current;

  const openDropdown = () => {
    setIsOpen(true);
    setHighlightedIndex(0);
  };

  const commitOption = (option: ICountryControlOptions) => {
    setSelectedOption(option);
    setQuery(option.label);
    setIsControlDirty(true);
    setIsOpen(false);
    onCountryChange?.(option.value as string);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) openDropdown();
        setHighlightedIndex(current => Math.min(current + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex(current => Math.max(current - 1, 0));
        break;
      case 'Enter': {
        event.preventDefault();
        const option = isOpen ? filteredOptions[highlightedIndex] : undefined;

        if (option) {
          commitOption(option);
          break;
        }
        onSubmit?.();
        break;
      }
      case 'Escape':
        closeDropdown();
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={classNames(styles.wrapper, className)}
      ref={wrapperRef}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className={classNames(styles.flag, styles.flagExtraStyle)}>
        <Country id={selectedOption?.countryId ?? selectedOption?.value.toString() ?? NO_COUNTRY_FLAG_ID} />
      </div>

      <div className={styles.control}>
        <input
          ref={inputRef}
          itemID={id}
          className={classNames(styles.input, { [styles.inputOpen]: isOpen })}
          type="text"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={isFocused ? '' : placeholder}
          value={query}
          onMouseDown={() => {
            // Toggle on click: pressing the input while the menu is open closes it (matches CommonDropdown).
            if (isOpen) {
              closeDropdown();
            } else {
              openDropdown();
            }
          }}
          onFocus={() => {
            setIsFocused(true);
            inputRef.current?.select();
          }}
          onBlur={() => setIsFocused(false)}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsControlDirty(true);
            openDropdown();
          }}
          onKeyDown={handleKeyDown}
        />

        <span className={classNames(styles.popupIcon, { [styles.popupIconOpen]: isOpen })}>
          <img
            id="countryChoiceIcon"
            aria-label="country choice drop down icon"
            src={icon || iconExpandDownCapital}
            alt="up down icon"
          />
        </span>

        {isMounted && (
          <div
            className={classNames(
              styles.paper,
              status === AnimatedMountStatus.Open ? styles.paperOpen : styles.paperClosing,
            )}
            role="listbox"
            id={listboxId}
          >
            {renderedOptions.length > 0 ? (
              renderedOptions.map((option: ICountryControlOptions, index: number) => (
                <ClickableDiv
                  key={option.value.toString()}
                  role="option"
                  aria-selected={selectedOption?.value === option.value}
                  className={classNames(styles.option, { [styles.optionHighlighted]: index === highlightedIndex })}
                  onClick={() => commitOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <Country
                    name={option.label as string}
                    id={(option.countryId ?? option.value) as string}
                    isSelected={selectedOption?.value === option.value}
                  />
                </ClickableDiv>
              ))
            ) : (
              <div className={styles.noOptions}>{query}</div>
            )}
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className={styles.errorContainer}>
          <img src={InputErrorIcon} alt="Error icon" className={styles.errorIcon} />
          <p key={errors[0].id} className={styles.capitalError}>
            {errors[0].error}
          </p>
        </div>
      )}
    </div>
  );
};

export default CountryControl;
